import { observer } from 'mobx-react-lite';
import { type ComponentType, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { DeliveryTimeWarning } from '~/components/HeaderV3/components/DeliveryTimeWarning/DeliveryTimeWarning';
import {
  CartIcon,
  HeartIcon,
  HomeIcon,
  SearchIcon,
  UserIcon,
} from '~/components/MarketplaceIcons';
import { UI_BOTTOM_COMPONENTS } from '~/constants/variables';
import { useMounted } from '~/hooks/useMounted';
import { ROUTES } from '~/pages/Root/constants';
import { cartStore } from '~/stores';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';

interface NavItemDE {
  key: string;
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  isActive: boolean;
  badge?: number;
}

const BADGE_MAX = 99;

const ITEM_CLASS =
  'relative flex h-11 w-[52px] items-center justify-center rounded-full no-underline! transition-colors duration-[120ms] ease-[cubic-bezier(.22,1,.36,1)]';

const NavigationBarDEView = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const mounted = useMounted();
  const cartRef = useRef<HTMLAnchorElement>(null);

  const cartCount = mounted ? cartStore.totalCartCount : 0;
  const favoritesCount = mounted ? favoritesStore.favoritesIds.length : 0;

  const items: NavItemDE[] = [
    {
      key: 'home',
      to: '/',
      label: t('mobileNav:home'),
      icon: HomeIcon,
      isActive: pathname === '/',
    },
    {
      key: 'search',
      to: ROUTES.search,
      label: t('searchProducts'),
      icon: SearchIcon,
      isActive: pathname === ROUTES.search || pathname === '/shop',
    },
    ...(company.isFavsTabEnabled
      ? [
          {
            key: 'favs',
            to: '/favs',
            label: t('mobileNav:favs'),
            icon: HeartIcon,
            isActive: pathname === '/favs',
            badge: favoritesCount,
          },
        ]
      : []),
    ...(company.hideCabinetRoutes
      ? []
      : [
          {
            key: 'cabinet',
            to: '/cabinet',
            label: t('mobileNav:cabinet'),
            icon: UserIcon,
            isActive: pathname.startsWith('/cabinet'),
          },
        ]),
    {
      key: 'cart',
      to: '/cart',
      label: t('mobileNav:cart'),
      icon: CartIcon,
      isActive: pathname === '/cart',
      badge: cartCount,
    },
  ];

  return (
    <div
      id={UI_BOTTOM_COMPONENTS.NAVBAR}
      data-company={company.name}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[70] flex justify-center pb-[calc(14px+env(safe-area-inset-bottom))]"
    >
      <nav
        aria-label={t('menu')}
        className="pointer-events-auto flex items-center gap-1 rounded-full border border-[#EDEDED] border-solid bg-[rgba(255,255,255,0.92)] p-1.5 text-[#141415] antialiased shadow-[0_8px_28px_-8px_rgba(20,20,21,0.22)] backdrop-blur-[12px] backdrop-saturate-[1.2] [font-family:var(--fontFamily2)]"
      >
        {items.map(({ key, to, label, icon: Icon, isActive, badge }) => (
          <Link
            key={key}
            ref={key === 'cart' ? cartRef : undefined}
            to={to}
            aria-label={badge ? `${label}, ${badge}` : label}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              ITEM_CLASS,
              isActive
                ? 'bg-[#FDEBE2] text-[#E94E1A]!'
                : 'bg-transparent text-[#141415]!',
            )}
          >
            <Icon className="size-[22px]" />
            {Boolean(badge) && (
              <span className="absolute top-1 right-2 h-4 min-w-4 rounded-[8px] bg-[#E94E1A] px-1 text-center font-semibold text-[11px] text-white leading-4">
                {badge && badge > BADGE_MAX ? `${BADGE_MAX}+` : badge}
              </span>
            )}
          </Link>
        ))}
        <DeliveryTimeWarning buttonRef={cartRef} />
      </nav>
    </div>
  );
};

export const NavigationBarDE = observer(NavigationBarDEView);
